import { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'
import type { PurchaseOrder, Requisition, GoodsReceiptListRow } from '../../../../Procurement/core/_models'

interface Props {
  recentPOs: PurchaseOrder[]
  recentReqs: Requisition[]
  recentGRNs: GoodsReceiptListRow[]
  loading: boolean
  openPRCount: number
  openPOCount: number
  grnPendingCount: number
  onViewPOs: () => void
  onViewReqs: () => void
  onViewGRNs: () => void
}

const statusBadge = (s?: string | null) => {
  if (!s) return 'bg-slate-100 text-slate-600'
  if (['COMPLETED', 'DELIVERED', 'FULFILLED', 'CLOSED', 'POSTED'].includes(s)) return 'bg-emerald-100 text-emerald-600'
  if (['REJECTED', 'CANCELLED', 'OVERDUE'].includes(s)) return 'bg-red-100 text-red-600'
  if (s.includes('PARTIAL')) return 'bg-amber-100 text-amber-600'
  return 'bg-blue-100 text-blue-600'
}

const fmtDate = (d?: string | null) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const poMeta: CardMeta = {
  description: 'Recent purchase orders showing PO number, vendor, amount, and status.',
  endpoint: 'GET /procurement/dashboard',
  logic: 'Uses the scoped purchaseOrders section from the integrated procurement dashboard response.',
}

const reqMeta: CardMeta = {
  description: 'Recent requisitions showing req number, title, vessel, required date, and status.',
  endpoint: 'GET /procurement/dashboard',
  logic: 'Uses the scoped requisitions section from the integrated procurement dashboard response.',
}

const grnMeta: CardMeta = {
  description: 'Recent goods receipts showing GRN number, PO reference, received date, and status.',
  endpoint: 'GET /procurement/dashboard',
  logic: 'Uses the scoped goodsReceipts section from the integrated procurement dashboard response.',
}

const ProcurementWidgets: FC<Props> = ({ recentPOs, recentReqs, recentGRNs, loading, openPRCount, openPOCount, grnPendingCount, onViewPOs, onViewReqs, onViewGRNs }) => {
  return (
    <>
      {/* Purchase Orders */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>Purchase Orders</h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={poMeta} />
            <button onClick={onViewPOs} className='text-[11px] text-blue-600 hover:underline'>View all</button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[280px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>PO No.</th>
                <th className='pb-2 font-medium'>Vendor</th>
                <th className='pb-2 font-medium'>Amount</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-20'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {recentPOs.length === 0 && !loading && (
                <tr><td colSpan={4} className='py-8 text-center text-slate-400'>No purchase orders</td></tr>
              )}
              {!loading && recentPOs.map((po) => (
                <tr
                  key={po.id}
                  onClick={onViewPOs}
                  className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                >
                  <td className='py-2 font-medium text-slate-800'>{po.poNumber || `PO-${po.id}`}</td>
                  <td className='py-2 text-slate-600'>{po.vendor?.name || '—'}</td>
                  <td className='py-2 text-slate-800'>{po.totalAmount != null ? `$ ${po.totalAmount.toLocaleString()}` : '—'}</td>
                  <td className='py-2'>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(po.status)}`}>
                      {po.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Requisitions */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>
            Requisitions <span className='text-red-500'>{loading ? '…' : openPRCount}</span>
          </h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={reqMeta} />
            <button onClick={onViewReqs} className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'>
              View all <ChevronRight className='w-3 h-3' />
            </button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[400px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>Req No.</th>
                <th className='pb-2 font-medium'>Title</th>
                <th className='pb-2 font-medium'>Vessel</th>
                <th className='pb-2 font-medium'>Required By</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-24'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {recentReqs.length === 0 && !loading && (
                <tr><td colSpan={5} className='py-8 text-center text-slate-400'>No requisitions</td></tr>
              )}
              {!loading && recentReqs.map((r) => (
                <tr
                  key={r.id}
                  onClick={onViewReqs}
                  className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                >
                  <td className='py-2 font-medium text-slate-800'>{r.requisition_code || `PR-${r.id}`}</td>
                  <td className='py-2 text-slate-600 max-w-[120px] truncate'>{r.title || '—'}</td>
                  <td className='py-2 text-slate-600'>{r.vesselName || '—'}</td>
                  <td className='py-2 text-slate-800'>{fmtDate(r.requiredDeliveryDate)}</td>
                  <td className='py-2'>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(r.status)}`}>
                      {r.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* GRN / Receipts */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>
            GRN / Receipts <span className='text-red-500'>{loading ? '…' : grnPendingCount}</span>
          </h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={grnMeta} />
            <button onClick={onViewGRNs} className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'>
              View all <ChevronRight className='w-3 h-3' />
            </button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[400px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>GRN No.</th>
                <th className='pb-2 font-medium'>PO No.</th>
                <th className='pb-2 font-medium'>Received</th>
                <th className='pb-2 font-medium'>Vessel</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {recentGRNs.length === 0 && !loading && (
                <tr><td colSpan={5} className='py-8 text-center text-slate-400'>No goods receipts</td></tr>
              )}
              {!loading && recentGRNs.map((g) => (
                <tr
                  key={g.id}
                  onClick={onViewGRNs}
                  className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                >
                  <td className='py-2 font-medium text-slate-800'>{`GRN-${g.id}`}</td>
                  <td className='py-2 text-slate-600'>{g.requisitionNo || `PO-${g.purchaseOrderId}`}</td>
                  <td className='py-2 text-slate-600'>{fmtDate(g.receivedDate)}</td>
                  <td className='py-2 text-slate-600'>—</td>
                  <td className='py-2'>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(g.status)}`}>
                      {g.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

export default ProcurementWidgets
